import { Context } from "common/types/context";
import { Warning } from "common/widgets/warning";
import { Selector } from "common/widgets/selector";
import { SchedulerFilter } from "common/types/scheduler-filters";
import { OnConfirmationOpen } from "common/widgets/warning/use-confirmation";
import { WithConfirmation } from "common/widgets/warning/with-confirmation";
import { getFilterLabel, getFormatOptionFn, getOptionValue } from "./functions";

type ConfigurationSelectorProps = {
  context: Context;
  filters: SchedulerFilter[];
  filter: SchedulerFilter;
  isFilterChanged: boolean;
  onFilterSelected: (filter: SchedulerFilter) => void;
};

type ConfigurationWarningProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const ConfirmationWarning = ({
  isOpen,
  onClose,
  onConfirm,
}: ConfigurationWarningProps) => {
  if (!isOpen) return null;

  return (
    <Warning
      title={_("Unsaved Changes Detected")}
      content={_(
        "You have unsaved changes in the current filter. Switching to a different filter will discard these changes. Would you like to proceed and lose your modifications, or stay and save them first?",
      )}
      action1={_("Stay And Review Changes")}
      action2={_("Discard Changes And Switch Filter")}
      onAction1={onClose}
      onAction2={onConfirm}
    />
  );
};

export function ConfigurationSelector({
  context,
  filters,
  filter,
  isFilterChanged,
  onFilterSelected,
}: Readonly<ConfigurationSelectorProps>) {
  const getHandleChangeFn =
    (onOpen: OnConfirmationOpen<void>) => (selectedFilter: SchedulerFilter) => {
      if (isFilterChanged) {
        onOpen()
          .then(() => {
            onFilterSelected(selectedFilter);
          })
          .catch(() => undefined);
      } else {
        onFilterSelected(selectedFilter);
      }
    };

  return (
    <WithConfirmation>
      {({ onOpen, onClose, onConfirm, isOpen }) => {
        return (
          <>
            <Selector<SchedulerFilter>
              className="qa-view-selector"
              placeholder={_("Saved views")}
              options={filters}
              allowClear={false}
              getOptionLabel={getFilterLabel}
              getOptionValue={getOptionValue}
              formatOption={getFormatOptionFn(
                context,
                isFilterChanged ? filter : undefined,
              )}
              value={filter}
              onChange={getHandleChangeFn(onOpen)}
            />
            <ConfirmationWarning
              isOpen={isOpen}
              onClose={onClose}
              onConfirm={onConfirm}
            />
          </>
        );
      }}
    </WithConfirmation>
  );
}
